import { useState } from 'react';
import { Badge, BlockStack, Button, Card, Icon, InlineStack, Page, Text } from '@shopify/polaris';
import { CheckSmallIcon } from '@shopify/polaris-icons';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import { useLegacyMode } from '../legacy/LegacyModeContext';
import { STOREFRONT_COMPONENTS } from '../../app/terminology/components';
import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
import { getPresentedPlanTier } from './planPresentation';

// Existing subscription keys stay stable; the proposed display names are Core, Pro, and Max.
type PlanId = 'standard' | 'growth' | 'pro';
type ComparisonRow = { label: string; description?: string; values: Record<PlanId, boolean | string> };
type FeatureSection = { title: string; features: string[] };

const shoppingHighlights = [
    { title: 'My Garage', description: 'Let shoppers save vehicles and switch between them while shopping.' },
    { title: STOREFRONT_COMPONENTS.fitment_notes.label, description: 'Help shoppers check whether a product fits their selected vehicle.' },
    { title: STOREFRONT_COMPONENTS.fitment_table.label, description: 'Show compatible vehicles directly on product pages.' },
    { title: 'Save vehicles to orders', description: 'Save the selected vehicle with each item added to the cart, carrying it through to the order.' },
];

// Proposed packaging only. Billing and feature entitlements are not changed by this page.
const fitmentAllowances: Record<PlanId, string> = { standard: '1 million', growth: '5 million', pro: '25 million' };
const perProductAllowance = 'No per-product cap, within your plan’s total fitment allowance.';
const plans: Array<{ id: PlanId; name: string; price: string; description: string; highlight?: string; includes: string; sections: FeatureSection[] }> = [
    {
        id: 'standard', name: 'PartFits Core', price: '$49', description: 'Fitment essentials for your Shopify theme',
        includes: 'All four shopping features above, plus:',
        sections: [
            { title: 'Storefront', features: ['Vehicle Selector with up to 8 fields', 'Filter products on your Shopify collection page', 'Send shoppers to a vehicle-mapped Shopify collection'] },
            { title: 'Catalog capacity', features: ['No catalog size limit without Turn14 sync', 'Turn14 fitment sync for catalogs up to 250,000 products'] },
            { title: 'Data & imports', features: ['CSV/XLSX imports, fitment replacement, and CSV export', 'Product-tag and vehicle-to-collection mapping imports'] },
            { title: 'Reports', features: ['Daily search volume and top vehicle search reports'] },
        ],
    },
    {
        id: 'growth', name: 'PartFits Pro', price: '$129', description: 'PartFits results, filters, and merchandising',
        highlight: perProductAllowance,
        includes: 'PartFits Core features, plus:',
        sections: [
            { title: 'Storefront', features: ['PartFits-built product results page', 'Variant-level fitments confirmed and displayed on product pages', 'Products grouped by collection or product type', 'Result filters: vendor, product type, collection, tags, price, availability, and connected product attributes (metafields)'] },
            { title: 'Catalog capacity', features: ['Up to 500,000 products'] },
            { title: 'Reports', features: ['Fitment coverage opportunity and health reports', 'Search demand comparison reports'] },
            { title: 'Integrations', features: ['Klaviyo vehicle events, current selection, and saved garage syncing (proposed packaging)'] },
        ],
    },
    {
        id: 'pro', name: 'PartFits Max', price: '$249', description: 'Complete feature access and specialized catalog tools',
        highlight: perProductAllowance,
        includes: 'Everything in PartFits Pro, plus:',
        sections: [
            { title: 'Storefront', features: ['Custom result groups combining Shopify collections'] },
            { title: 'Catalog capacity', features: ['Up to 1,000,000 products'] },
            { title: 'Data & imports', features: ['VCDB vehicle reference database', 'ACES XML imports'] },
        ],
    },
];

const proAndMax = { standard: false, growth: true, pro: true };
const maxOnly = { standard: false, growth: false, pro: true };
const comparisonSections: Array<{ title: string; differences: ComparisonRow[]; shared: string[] }> = [
    { title: 'Catalog capacity', differences: [
    { label: 'Active fitment assignments', description: 'Proposed total allowance across your catalog, alongside the product limits below.', values: fitmentAllowances },
    { label: 'Catalog size (products)', values: { standard: 'No limit without Turn14 sync; up to 250,000 with Turn14 sync', growth: 'Up to 500,000', pro: 'Up to 1,000,000' } },
    { label: 'Fitments per product', description: 'With PartFits-built product results, there is no per-product cap within your plan’s total fitment allowance. Plan catalog size limits also apply. Product-tag filtering shares Shopify’s 250-tag allowance with other product tags; Shopify Plus is exempt. This restriction applies to tag-based filtering on every plan, not to vehicle-to-collection redirects.', values: { standard: 'Up to 250 for product-tag filtering, subject to available tags; Shopify Plus exempt', growth: perProductAllowance, pro: perProductAllowance } },
    ], shared: ['One fitment catalog'] },
    { title: 'Storefront & shopping', differences: [
    { label: 'Configurable selector fields', values: { standard: 'Up to 8', growth: 'Up to 8', pro: 'Up to 8' } },
    { label: 'PartFits-built product results page', description: 'Display matching product cards on a dedicated results page provided by PartFits.', values: proAndMax },
    { label: 'Variant-level fitments', description: 'Assign fitments to individual variants and show shoppers whether their selected variant fits on the product page. Included in PartFits Pro and Max.', values: proAndMax },
    { label: 'Product results grouped by collection or product type', description: 'Show matching products under collection or product-type headings on the same page.', values: proAndMax },
    { label: 'PartFits result filters', description: 'Let shoppers narrow PartFits-built product results by vendor, product type, collection, tags, price, availability, or a connected product attribute (metafield). Included in PartFits Pro and Max.', values: proAndMax },
    { label: 'Custom result groups combining Shopify collections', values: maxOnly },
    ], shared: [
        ...shoppingHighlights.map(feature => feature.title),
        'Persistent vehicle selection',
        'Product filtering on your Shopify collection page',
        'Redirects to vehicle-mapped Shopify collections',
        'Theme app extension and basic storefront styling',
        'Fitment notes',
        'Universal products',
    ] },
    { title: 'Data & integrations', differences: [
    { label: 'Klaviyo vehicle events, current selection, and saved garage syncing', description: 'Proposed PartFits Pro and Max packaging. Setup is currently available without plan restrictions.', values: proAndMax },
    { label: 'VCDB reference access for building your own vehicle mappings', values: maxOnly },
    { label: 'ACES XML import', values: maxOnly },
    ], shared: [
    'Turn14 fitment sync (requires a connected Turn14 account)',
    'Product-level fitment mapping',
    'CSV/XLSX fitment import, including Turn14-compatible files',
    'CSV/XLSX fitment replacement for matched SKUs',
    'Product-tag import with preview, review, and undo',
    'Vehicle-to-collection mapping import',
    'CSV fitment export',
    ] },
    { title: 'Reports & insights', differences: [
        { label: 'Fitment coverage opportunity report', description: 'Find frequently searched vehicles with few or no mapped products, using current catalog coverage.', values: proAndMax },
        { label: 'Search demand comparison reports', description: 'Compare search activity across equal-length periods to spot changes in demand.', values: proAndMax },
        { label: 'Fitment coverage health report', description: 'Identify vehicles with missing or low product coverage across your active fitment catalog.', values: proAndMax },
    ], shared: ['Daily search volume report', 'Top vehicle searches report'] },
];

const resolveCurrentPlan = (name?: string): PlanId | undefined => {
    const tier = getPresentedPlanTier(name);
    return tier === 'core' ? 'standard' : tier === 'pro' ? 'growth' : tier === 'max' ? 'pro' : undefined;
};

const FeatureList = ({ features }: { features: string[] }) => (
    <ul className="pf-plan-feature-list">
        {features.map(feature => <li key={feature}>
            <span className="pf-plan-feature-list__icon" aria-hidden="true"><Icon source={CheckSmallIcon} /></span>
            <Text as="span" variant="bodyMd">{feature}</Text>
        </li>)}
    </ul>
);

const PlanPage = ({ embedded = false }: { embedded?: boolean }) => {
    const { userStore } = useStore();
    const { isLegacy } = useLegacyMode();
    const subscription = userStore.user?.subscription;
    const currentPlanId = resolveCurrentPlan(subscription?.name);
    const isActive = subscription?.active === true;
    const currentName = subscription?.name?.trim() || (isLegacy ? 'Your current plan' : 'Plan details unavailable');
    const [showAllFeatures, setShowAllFeatures] = useState(false);
    const recommendPro = !subscription || (isActive && currentPlanId === 'standard');
    const contactUrl = `mailto:support@govedia.com?subject=${encodeURIComponent('PartFits custom capacity, integration, or migration')}&body=${encodeURIComponent(
        `Store: ${userStore.user?.shopDomain || ''}\n\nTell us about your fitment volume, usage, data sources, and catalog requirements:\n`
    )}`;

    const renderValue = (value: boolean | string) => typeof value === 'string'
        ? value
        : value
            ? <span className="pf-plan-comparison__included" role="img" aria-label="Included"><Icon source={CheckSmallIcon} /></span>
            : <span className="pf-plan-comparison__unavailable" role="img" aria-label="Not included">&mdash;</span>;

    const content = (
        <div className="pf-plan-page">
            <BlockStack gap="500">
                <Card>
                    <BlockStack gap="200">
                        <InlineStack gap="200" blockAlign="center">
                            <Text as="h2" variant="headingSm">{isActive ? 'Current PartFits subscription' : 'PartFits subscription'}: {currentName}</Text>
                            {subscription && <Badge tone={isActive ? (subscription.trialActive ? 'info' : 'success') : undefined}>
                                {isActive ? (subscription.trialActive ? 'Trial' : 'Active') : 'Inactive'}
                            </Badge>}
                        </InlineStack>
                        <Text as="p" tone="subdued">Your existing access and billing remain unchanged.</Text>
                        <Text as="p" tone="subdued">Existing PFV1 merchants retain their current results experience, including legacy collection results.</Text>
                    </BlockStack>
                </Card>

                {/* Phase 1: migrated PFV1 stores keep their plan, so no upgrade content. */}
                {!isLegacy && <>
                <section aria-labelledby="plan-fitment-heading">
                    <Card>
                        <BlockStack gap="200">
                            <InlineStack><Badge tone="info">Included in PartFits Pro and Max</Badge></InlineStack>
                            <Text id="plan-fitment-heading" as="h2" variant="headingLg">Fitment capacity for broad vehicle coverage</Text>
                            <Text as="p">Sell products that fit hundreds or thousands of vehicles. PartFits-built product results use your fitment database, so Shopify’s product-tag limit does not cap the number of vehicles each product can fit.</Text>
                            <Text as="p" fontWeight="bold">{perProductAllowance}</Text>
                            <Text as="p" variant="bodySm" tone="subdued">With PartFits-built product results. Your plan’s catalog size limit also applies.</Text>
                        </BlockStack>
                    </Card>
                </section>

                <section aria-labelledby="plan-shopping-heading">
                    <Card>
                        <BlockStack gap="400">
                            <div className="pf-plan-section-heading">
                                <Text id="plan-shopping-heading" as="h2" variant="headingLg">Help shoppers find the right fit</Text>
                                <Text as="p" tone="subdued">These shopping features are included in every plan, starting with PartFits Core.</Text>
                            </div>
                            <div className="pf-plan-shopping-grid">
                                {shoppingHighlights.map(feature => <div className="pf-plan-shopping-feature" key={feature.title}>
                                    <Text as="h3" variant="headingMd">{feature.title}</Text>
                                    <Text as="p" tone="subdued">{feature.description}</Text>
                                </div>)}
                            </div>
                        </BlockStack>
                    </Card>
                </section>

                <section aria-labelledby="plan-options-heading">
                    <BlockStack gap="400">
                        <div className="pf-plan-section-heading">
                            <InlineStack gap="200" blockAlign="center">
                                <Text id="plan-options-heading" as="h2" variant="headingLg">Plans at a glance</Text>
                                <Badge tone="info">Plan preview</Badge>
                            </InlineStack>
                            <Text as="p" tone="subdued">
                                Preview proposed pricing, features, and fitment allowances. Fitment allowances are subject to validation of storage, search load, and import frequency. Plan changes are not available yet; these limits do not change your current access.
                            </Text>
                        </div>
                        <div className="pf-plan-grid">
                            {plans.map(plan => {
                                const isCurrent = isActive && currentPlanId === plan.id;
                                const isRecommended = plan.id === 'growth' && recommendPro;
                                return <article key={plan.id} aria-labelledby={`plan-${plan.id}-heading`}
                                    className={`pf-plan-card${isCurrent ? ' pf-plan-card--current' : ''}${isRecommended ? ' pf-plan-card--recommended' : ''}`}>
                                    <Card>
                                        <div className="pf-plan-card__content">
                                            <BlockStack gap="200">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Text id={`plan-${plan.id}-heading`} as="h3" variant="headingLg">{plan.name}</Text>
                                                    {isCurrent && <Badge>Current plan</Badge>}
                                                    {isRecommended && <Badge tone="info">Recommended</Badge>}
                                                </InlineStack>
                                                <Text as="p" tone="subdued">{plan.description}</Text>
                                                <div className="pf-plan-card__price"><span>{plan.price}</span><small>USD / month</small></div>
                                                <BlockStack gap="100">
                                                    <Text as="p" variant="headingMd">{fitmentAllowances[plan.id]} active fitment assignments</Text>
                                                    <Text as="p" variant="bodySm" tone="subdued">Included across your catalog (proposed).</Text>
                                                </BlockStack>
                                                {plan.highlight && <BlockStack gap="100">
                                                    <Text as="p" fontWeight="bold">{plan.highlight}</Text>
                                                    <Text as="p" variant="bodySm" tone="subdued">With PartFits-built product results.</Text>
                                                </BlockStack>}
                                            </BlockStack>
                                            <div className="pf-plan-card__divider" />
                                            <Text as="p" tone="subdued">{plan.includes}</Text>
                                            {plan.sections.map(section => <BlockStack key={section.title} gap="200">
                                                <Text as="h4" variant="headingSm">{section.title}</Text>
                                                <FeatureList features={section.features} />
                                            </BlockStack>)}
                                            <div className="pf-plan-card__action">
                                                <Button fullWidth disabled>{isCurrent ? 'Current plan' : 'Plan changes unavailable'}</Button>
                                            </div>
                                        </div>
                                    </Card>
                                </article>;
                            })}
                        </div>
                        <Card>
                            <BlockStack gap="200">
                                <Text as="h3" variant="headingMd">How the proposed fitment allowance is counted</Text>
                                <Text as="p">A fitment assignment links a product or variant to a vehicle. The allowance counts active, unique assignments across your catalog, rather than the number of vehicles alone.</Text>
                                <Text as="p">Deleted records, audit history, duplicate imports, and internal copies are excluded. Reimporting the same data adds no usage. Universal products are not counted as an assignment to every vehicle.</Text>
                                <Text as="p" variant="bodySm" tone="subdued">These are proposed counting rules; fitment allowances are not currently enforced.</Text>
                            </BlockStack>
                        </Card>
                        <Text as="p" variant="bodySm" tone="subdued">
                            Product limits are separate from total fitment allowances. PartFits Core has no catalog size limit unless Turn14 fitment sync is enabled, when its catalog limit is 250,000 products. PartFits Pro supports up to 500,000 products and PartFits Max up to 1,000,000, with or without Turn14 sync.
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                            Turn14 fitment sync requires a connected Turn14 account. Every plan includes the vehicle information needed for those fitments. PartFits Max adds independent VCDB reference access for building your own mappings.
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                            CSV/XLSX imports are included in every plan, with no monthly import quota. ACES XML imports are included in PartFits Max.
                        </Text>
                    </BlockStack>
                </section>

                <Card>
                    <div className="pf-plan-contact">
                        <BlockStack gap="100">
                            <Text as="h2" variant="headingMd">Custom — quoted pricing</Text>
                            <Text as="p" tone="subdued">For more than 25 million active fitment assignments, unusually heavy usage, or synced catalogs above 1,000,000 products, contact us to discuss your requirements.</Text>
                            <Text as="p" tone="subdued">We can also discuss custom integrations and migration support.</Text>
                        </BlockStack>
                        <Button url={contactUrl}>Contact us</Button>
                    </div>
                </Card>

                <section aria-labelledby="plan-feature-comparison-heading">
                    <BlockStack gap="400">
                        <div className="pf-plan-section-heading">
                            <Text id="plan-feature-comparison-heading" as="h2" variant="headingLg">Compare plan features</Text>
                            <Text as="p" tone="subdued">See what each proposed plan adds.</Text>
                        </div>
                        <div className="pf-plan-comparison" role="region" aria-label="Plan feature comparison">
                            <table>
                                <caption className="pf-plan-visually-hidden">Proposed PartFits Core, Pro, and Max features</caption>
                                <colgroup><col style={{ width: '40%' }} />{plans.map(plan => <col key={plan.id} style={{ width: '20%' }} />)}</colgroup>
                                <thead><tr>
                                    <th scope="col">Feature</th>
                                    {plans.map(plan => <th scope="col" key={plan.id}>{plan.name}</th>)}
                                </tr></thead>
                                {comparisonSections.map(section => <tbody key={section.title}>
                                    <tr className="pf-plan-comparison__section-row"><th colSpan={4} scope="colgroup">{section.title}</th></tr>
                                    {section.differences.map(row => <tr key={row.label}>
                                    <th scope="row">
                                        {row.label}
                                        {row.description && <Text as="p" variant="bodySm" tone="subdued" fontWeight="regular">{row.description}</Text>}
                                    </th>
                                    {plans.map(plan => <td key={plan.id}>
                                        <span className="pf-plan-comparison__mobile-label" aria-hidden="true">{plan.name}</span>
                                        {renderValue(row.values[plan.id])}
                                    </td>)}
                                </tr>)}
                                    {showAllFeatures && section.shared.map(feature => <tr key={feature}>
                                        <th scope="row">{feature}</th>
                                        {plans.map(plan => <td key={plan.id}>
                                            <span className="pf-plan-comparison__mobile-label" aria-hidden="true">{plan.name}</span>
                                            {renderValue(true)}
                                        </td>)}
                                    </tr>)}
                                </tbody>)}
                            </table>
                        </div>
                        <InlineStack><Button variant="plain" disclosure={showAllFeatures ? 'up' : 'down'}
                            ariaExpanded={showAllFeatures} onClick={() => setShowAllFeatures(value => !value)}>
                            {showAllFeatures ? 'Show plan differences only' : 'Show full comparison'}
                        </Button></InlineStack>
                    </BlockStack>
                </section>
                </>}

            </BlockStack>
        </div>
    );

    return embedded ? content : <Page title={SETTINGS_SECTIONS.plan} subtitle={isLegacy ? "Review your subscription." : "Review your subscription and explore plans."} fullWidth>{content}</Page>;
};

export default observer(PlanPage);
